import { type Pitch, type Step, type Alter, pitchClassFromFifths, pitchClassName, mod } from './pitch';

export type Mode = 'major' | 'minor' | 'dorian' | 'phrygian' | 'lydian' | 'mixolydian' | 'locrian';
export const MODES: readonly Mode[] = ['major', 'minor', 'dorian', 'phrygian', 'lydian', 'mixolydian', 'locrian'];

export interface KeySig {
  fifths: number; // -7..7
  mode: Mode;
}

export const SHARP_ORDER: readonly Step[] = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
export const FLAT_ORDER: readonly Step[] = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];

/** The 15 key signatures in the order of the key-signature dialog. */
export const KEY_GRID: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, -1, -2, -3, -4, -5, -6, -7];

/** Alteration a key signature applies to a letter. */
export function keyAlterFor(fifths: number, step: Step): Alter {
  if (fifths > 0) return SHARP_ORDER.indexOf(step) < fifths ? 1 : 0;
  if (fifths < 0) return FLAT_ORDER.indexOf(step) < -fifths ? -1 : 0;
  return 0;
}

/** Line-of-fifths offset of each mode's tonic from the relative major tonic. */
export const MODE_TONIC_OFFSET: Readonly<Record<Mode, number>> = {
  lydian: -1,
  major: 0,
  mixolydian: 1,
  dorian: 2,
  minor: 3,
  phrygian: 4,
  locrian: 5,
};

export function keyTonic(k: KeySig): { step: Step; alter: Alter } {
  const pc = pitchClassFromFifths(k.fifths + MODE_TONIC_OFFSET[k.mode]);
  return { step: pc.step, alter: Math.max(-2, Math.min(2, pc.alter)) as Alter };
}

const MODE_LABEL: Record<Mode, string> = {
  major: 'major',
  minor: 'minor',
  dorian: 'Dorian',
  phrygian: 'Phrygian',
  lydian: 'Lydian',
  mixolydian: 'Mixolydian',
  locrian: 'Locrian',
};

/** "G major", "E minor", "D Dorian". */
export function keyName(k: KeySig, unicode = true): string {
  return `${pitchClassName(keyTonic(k), unicode)} ${MODE_LABEL[k.mode]}`;
}

/** Major key name for a fifths count, as shown in the key grid: "F♯", "B♭". */
export function majorKeyLabel(fifths: number, unicode = true): string {
  return pitchClassName(keyTonic({ fifths, mode: 'major' }), unicode);
}

/** True if the pitch is diatonic to the key (ignoring octave). */
export function isDiatonic(p: Pick<Pitch, 'step' | 'alter'>, fifths: number): boolean {
  return keyAlterFor(fifths, p.step) === p.alter;
}

/**
 * Bring a fifths count into −7…7, choosing the enharmonic key with fewer
 * accidentals. Returns the shift applied (a multiple of 12).
 */
export function normalizeFifths(fifths: number): { fifths: number; shift: number } {
  const cands: number[] = [];
  for (let k = -4; k <= 4; k++) {
    const f = fifths + 12 * k;
    if (f >= -7 && f <= 7) cands.push(f);
  }
  let best = cands[0];
  for (const c of cands) {
    if (Math.abs(c) < Math.abs(best)) best = c;
  }
  // Prefer the original on a tie (F♯ vs G♭).
  if (cands.includes(fifths) && Math.abs(fifths) === Math.abs(best)) best = fifths;
  return { fifths: best, shift: best - fifths };
}

/** Diatonic scale degree (1-7) of a letter in a key, measured from the mode's tonic. */
export function scaleDegree(p: Pick<Pitch, 'step'>, k: KeySig): number {
  const tonic = keyTonic(k);
  const order = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  return mod(order.indexOf(p.step) - order.indexOf(tonic.step), 7) + 1;
}

/** Chromatic alteration of a pitch relative to its key-signature value. */
export function chromaticAlteration(p: Pick<Pitch, 'step' | 'alter'>, fifths: number): number {
  return p.alter - keyAlterFor(fifths, p.step);
}

